import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import NavItem from './NavItem';
import ProductsDropdown from './ProductsDropdown';

interface NavigationProps {
  isAuthenticated: boolean;
  onLogout: () => void;
}

const Navigation: React.FC<NavigationProps> = ({ isAuthenticated, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isUserPanel = location.pathname === '/user-panel';

  const handleAuthClick = () => {
    if (isAuthenticated) {
      if (isUserPanel) {
        // If we're on the user panel, send download request
        fetch('/download', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then(response => {
          if (!response.ok) {
            throw new Error('Download failed');
          }
          // Get filename from Content-Disposition header
          const contentDisposition = response.headers.get('Content-Disposition');
          const filenameMatch = contentDisposition && contentDisposition.match(/filename="(.+)"/);
          const filename = filenameMatch ? filenameMatch[1] : 'download.zip';
          
          return response.blob().then(blob => ({ blob, filename }));
        })
        .then(({ blob, filename }) => {
          // Create a URL for the blob
          const url = window.URL.createObjectURL(blob);
          // Create a temporary link element
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          // Clean up
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch(error => {
          console.error('Error initiating download:', error);
        });
      } else {
        // If we're elsewhere, show user panel button
        navigate('/user-panel');
      }
    } else {
      navigate('/login');
    }
  };

  return (
    <nav className="border-b border-gray-800 bg-gray-900/50 backdrop-blur-sm fixed w-full z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo Group */}
          <div className="flex items-center space-x-6">
            <div 
              className="w-12 h-12 cursor-pointer"
              onClick={() => navigate('/')}
            >
              <img 
                src="/images/logo.png" 
                alt="Recoil.club Logo" 
                className="w-full h-full object-contain drop-shadow-[0_0_20px_rgba(255,255,255,0.4)] [text-shadow:0_0_15px_rgba(255,255,255,0.4)]"
              />
            </div>
          </div>

          {/* Main Nav Items */}
          <div className="flex-1 flex justify-center space-x-12">
            <NavItem onClick={() => navigate('/')}>Home</NavItem>
            <ProductsDropdown />
            <NavItem onClick={() => window.open('https://discord.gg/K74ED3Tz2G', '_blank')}>Discord</NavItem>
            <NavItem>Guide</NavItem>
          </div>

          {/* Auth Button */}
          <div>
            <button 
              className="bg-gradient-to-r from-blue-400 to-blue-500 text-white px-5 py-1.5 rounded text-sm font-medium hover:from-blue-500 hover:to-blue-600 transition-all duration-200"
              onClick={handleAuthClick}
            >
              {isAuthenticated 
                ? (isUserPanel ? 'DOWNLOAD' : 'USER PANEL')
                : 'LOG IN'
              }
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation; 